import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { CasingPlan2dDetailBaseComponent } from '../detail/casing-plan-2d-detail-base.component';

import { HorizontalDrawingParts } from "./horizontal.drawing.parts.enum";
import { ComplexDrawObject } from "./complex-draw-object.model";
import { SimpleDrawObject } from "./simple-draw-object.model";

@Component({
    selector: 'casing-plan-2d-detail-horizontal',
    templateUrl: './casing-plan-2d-detail.component.html',
    styleUrls: ['./casing-plan-2d-detail.component.scss', './casing-plan-2d-detail-horizontal.component.scss'],
    standalone: false
})
export class CasingPlan2dDetailHorizontalComponent extends CasingPlan2dDetailBaseComponent implements OnInit {

    
    halfCasingWidthPx: number = 30;             //standard 1/2 width of casing in pixels
    rightCanvasMarginPx: number = 30;           //canvas margin right in pixels, room for bottom outline
    boreHoleCurveRadiusPx: number = 100;        //standard borehole curve radius in pixels
    cementLineWidthPx: number = 20;             //width of cement line in pixels
    firstKickpoint: any;                        //first kickpoint found in string (number or undefined)
    positionRelative: boolean = false;          //position collar, basket, centralizer relative to each other. If false, they are positioned exactly based on their depth 
    curveTopPx: number = 0;                     //start of curve in pixels from TOP
    curveEndPx: number = 0;                     //end of curve in pixels from LEFT
    verticalToCurvePx: number = 0;              //length of string in pixels ABOVE curve
    horizontalAfterCurvePx: number = 0;         //length of string in pixels AFTER curve
    horizontalTopPx: number = 0                 //top of horizontal string in pixels from TOP
    casingObject: ComplexDrawObject = new ComplexDrawObject; //object to draw casing or cement, all done in canvas pixels
    cementObject: ComplexDrawObject = new ComplexDrawObject;
    leading: number = 15;                       //line height (where used) in pixels
    bottomCementWidth = 20;                     //width of cement fill at bottom of the string
    aryKickpoint = [];                          //array of all kickpoints in string
    verticalTextOffset: number = 4;             //vertical offset to get text in middle of location & leader

    constructor() {
        super();
    }

    ngOnInit() {

        this.leftArrow.src = "../../../../../assets/images/SVG/arrow-left.svg";
        this.rightArrow.src = "../../../../../assets/images/SVG/arrow-right.svg";
        this.direction = "horizontal"; 

        //get the first kickpoint
        this.firstKickpoint = this.getFirstSegmentKickpoint();
        this.updateCanvas();
    }

    /**
     * Main method to draw canvas shapes & text
     */
    updateCanvas(): void {
        //set component vars
        this.setVariables();

        this.addWhiteBackground();

        if (this.firstKickpoint) {
            if (this.hasCement(this.segment)) {
                //add bottom block of cement
                this.addHorizontalCementBottom();

                //add colored cement lines
                this.addHorizontalCementFill();
            }            

            //add end cap to horizotal portion
            this.addHorizontalEndCap();

            if (this.hasBorehole(this.segment)) {
                //add borehole with curve (outside)
                this.addHorizontalBorehole();
            }
            

            if (this.hasCasing(this.segment)) {
                //add casing and labels (inside)
                this.addHorizontalCasing(this.casingObject);
            }            

            //dev only - displays grid for figuring out positioning
            //this.addDisplayLines();

            //add top ground lines
            this.addGround(this.segmentIndex);

            //add kickpoint label & leader
            this.addKickPointLabel();

            //add diameter labels & text
            this.addDiameterArrows(this.segment);

            //add vertical extensions to arrows
            this.addDashedLines();

        }
        else {
            this.showMissingKickpoint();
            console.log("No kickpoint provided for this horizontal string!");
        }
    }

    /**
     * Only shown if we get to here and there is no kick point for the string
     */
    showMissingKickpoint(): void {
        this.ctx.beginPath();
        this.ctx.font = "18px Work Sans";
        this.ctx.fillStyle = "#ff0000";
        this.ctx.textAlign = "center";
        this.ctx.fillText(this.dataMissingError, this.canvasWidth * .5, this.canvasHeight * .5);
        this.ctx.closePath();
    }

    /**
     * Set reusable variables
     */
    setVariables(): void {
        this.bottomCanvasMarginPx = 200;         //canvas bottom margin in pixels - space for curve, shoes and bottom info box
        //set h/w
        this.canvasWidth = this.canvasRef.nativeElement.width;
        this.canvasHeight = this.canvasRef.nativeElement.height - 5;
        //set factor for converting feet to pixels
        this.setfeetToPixelFactor(this.segment);
        //set canvas
        this.ctx = this.canvasRef.nativeElement.getContext('2d');
        //set center
        this.center = (this.canvasWidth / 2) - 25;
        //set right casing pos
        this.rightCasing = this.center + this.halfCasingWidthPx;
        //set left casing pos
        this.leftCasing = this.center - this.halfCasingWidthPx;
        //set right borehole
        this.rightBorehole = this.rightCasing + this.diagramCasingWidth;
        //set left borehole
        this.leftBorehole = this.leftCasing - this.diagramCasingWidth;
        //set casing length (obsolete!)
        this.casingLength = ((this.segment.casingBottom - this.segment.casingTop) * this.factorFeetToPixels) + this.canvasMarginTop - this.bottomCanvasMarginPx;
        //position of curve top from TOP
        this.curveTopPx = this.canvasHeight - this.bottomCanvasMarginPx;
        //position of curve end from LEFT
        this.curveEndPx = this.rightBorehole + this.boreHoleCurveRadiusPx;
        //vertical length ABOVE curve
        this.verticalToCurvePx = this.curveTopPx - this.canvasMarginTop;
        //horizontal length AFTER curve
        this.horizontalAfterCurvePx = this.canvasWidth - this.rightCanvasMarginPx - this.curveEndPx;
        //top of horizontal from TOP
        this.horizontalTopPx = this.verticalToCurvePx + this.boreHoleCurveRadiusPx + this.canvasMarginTop;
        //create array for collars, packers, etc. placement
        this.createPlacementArray();
        //array of kick points
        this.createKickpointArray();
    }

    /**
     * For Dev Only, adds grid to help with positioning
     */
    addDisplayLines(): void {

        this.ctx.beginPath();
        this.ctx.lineWidth = 1;
        this.ctx.strokeStyle = "#999999";
        this.ctx.moveTo(0, 0);
        this.ctx.lineTo(this.canvasWidth, 0);
        this.ctx.lineTo(this.canvasWidth, this.canvasHeight);
        this.ctx.lineTo(0, this.canvasHeight);
        this.ctx.closePath();
        this.ctx.stroke();

        //0, 0
        this.ctx.beginPath();
        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        this.ctx.fillText("0,0",5,10);
        this.ctx.closePath();

        //Margin Top
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#ff0000";
        this.ctx.moveTo(0, this.canvasMarginTop);
        this.ctx.lineTo(this.canvasWidth, this.canvasMarginTop)
        this.ctx.stroke();
        this.ctx.closePath();

        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        //text, x, y
        this.ctx.fillText("From Top: " + this.canvasMarginTop, 5, this.canvasMarginTop - 10);
        this.ctx.fillText("(canvasMarginTop)", 5, this.canvasMarginTop + 20);

        //Margin Bottom
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#ff0000";
        this.ctx.moveTo(0, this.canvasHeight - this.bottomCanvasMarginPx);
        this.ctx.lineTo(this.canvasWidth, this.canvasHeight - this.bottomCanvasMarginPx)
        this.ctx.stroke();
        this.ctx.closePath();

        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        this.ctx.fillText("From Top: " + (this.canvasHeight - this.bottomCanvasMarginPx), 5, this.canvasHeight - this.bottomCanvasMarginPx - 5);
        this.ctx.fillText("(bottomMargin)", 5, this.canvasHeight - this.bottomCanvasMarginPx + 15);

        //Vertical Center
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#ff0000";
        this.ctx.moveTo(this.center, 0);
        this.ctx.lineTo(this.center, this.canvasHeight);
        this.ctx.stroke();
        this.ctx.closePath();
        this.ctx.textAlign = "right";
        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        this.ctx.fillText("(center)", this.center - 5, 30);
        this.ctx.fillText("From Left: " + this.center, this.center - 5, 15);

        //rightCanvasMargin
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#ff0000";
        this.ctx.moveTo(this.canvasWidth - this.rightCanvasMarginPx, 0);
        this.ctx.lineTo(this.canvasWidth - this.rightCanvasMarginPx, this.canvasHeight);
        this.ctx.stroke();
        this.ctx.closePath();

        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        this.ctx.textAlign = "right";
        this.ctx.fillText("From Left: " + (this.canvasWidth - this.rightCanvasMarginPx), this.canvasWidth - this.rightCanvasMarginPx - 5, 15);
        this.ctx.fillText("(rightCanvasMargin)", this.canvasWidth - this.rightCanvasMarginPx - 5, 30);

        //end of curve
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#ff0000";
        this.ctx.moveTo(this.rightBorehole + this.boreHoleCurveRadiusPx, 0);
        this.ctx.lineTo(this.rightBorehole + this.boreHoleCurveRadiusPx, this.canvasHeight);
        this.ctx.stroke();
        this.ctx.closePath();


        this.ctx.font = this.font;
        this.ctx.fillStyle = "#ff0000";
        this.ctx.textAlign = "right";
        this.ctx.fillText("From Left: " + (this.rightBorehole + this.boreHoleCurveRadiusPx), this.rightBorehole + this.boreHoleCurveRadiusPx - 5, 15);
        this.ctx.fillText("curve end", this.rightBorehole + this.boreHoleCurveRadiusPx - 5, 30);


        //bottom coords
        this.ctx.fillText("550, 600", this.canvasWidth - 5, this.canvasHeight -5);
        this.ctx.strokeStyle = "#000000";
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.textAlign = "left";
    }

    /**
     * Creates sorted array of all string kick points
     */
    createKickpointArray(): void {
        if (this.segment && this.segment.gisObjects) {
            for (let gisObject of this.segment.gisObjects) {
                if (gisObject.point == "Kick Point") {
                    this.aryKickpoint.push(gisObject.tvd);
                }
            }
        }
        //sort ascending
        this.aryKickpoint.sort(function (a, b) {
            return a - b
        })

        
    }

    /**
     * Main array of drawing objects and their positions
     * NEEDS major overhaul, leaving for now (3/18/19)
     */
    createPlacementArray(): void {
        let aryPlacement = [];

        //populate placement array
        if (this.hasValidValue(this.segment.basketDepth, "number")) {
            let basketObject = this.getSimpleDrawObject(HorizontalDrawingParts.basket, this.segment.basketDepth);
            aryPlacement.push(basketObject);
        }
        if (this.hasValidValue(this.segment.floatCollarDepth, "number")) {
            let collarObject = this.getSimpleDrawObject(HorizontalDrawingParts.collar, this.segment.floatCollarDepth);
            aryPlacement.push(collarObject);
        }
        if (this.hasValidValue(this.segment.packerDepth, "number")) {
            let packerObject = this.getSimpleDrawObject(HorizontalDrawingParts.packer, this.segment.packerDepth);
            aryPlacement.push(packerObject);
        }

        //4/1/19 - shoes forced to fall at the end of the casing
        if (this.hasValidValue(this.segment.casingShoeDepth, "number")) {
            let shoeObject = this.getSimpleDrawObject(HorizontalDrawingParts.shoe, this.segment.casingBottom);
            aryPlacement.push(shoeObject);
        }

        //CASING
        if (this.hasValidValue(this.segment.casingTop, "number") && this.hasValidValue(this.segment.casingBottom, "number")) {

            this.casingObject.bottomFt = this.segment.casingBottom;

            this.casingObject.bottomBeforeKickpoint = this.segment.casingBottom <= this.firstKickpoint;
            if (this.casingObject.bottomBeforeKickpoint) {
                this.casingObject.bottomPx = ((this.segment.casingBottom / this.firstKickpoint) * this.verticalToCurvePx) + this.canvasMarginTop;
            }

            this.casingObject.bottomAfterKickpoint = this.segment.casingBottom > this.firstKickpoint;
            if (this.casingObject.bottomAfterKickpoint) {
                const drawingBottom = this.hasBorehole(this.segment) ? this.segment.boreholeBottom : this.segment.casingBottom;
                this.casingObject.bottomPx = ((this.segment.casingBottom - this.firstKickpoint) / (drawingBottom - this.firstKickpoint) * this.horizontalAfterCurvePx) + this.curveEndPx;
            }
            this.casingObject.bottomEndBeforeBoreHoleEnd = this.segment.casingBottom < this.segment.boreholeBottom;

            this.casingObject.topFt = this.segment.casingTop;
            this.casingObject.topBeforeKickpoint = this.segment.casingTop < this.firstKickpoint;
            if (this.casingObject.topBeforeKickpoint) {
                //override 3/15/19 - casing top is always at borehole top pixel location
                this.casingObject.topPx = this.canvasMarginTop;
                //actual calc
                //this.casingObject.topPx = ((this.segment.casingTop / this.firstKickpoint) * this.verticalToCurvePx) + this.canvasMarginTop;
            }
            this.casingObject.topEndBeforeKickpoint = this.segment.casingBottom < this.firstKickpoint;

        }

        //CEMENT
        if (this.hasValidValue(this.segment.cementTop, "number") && this.hasValidValue(this.segment.cementBottom, "number")) {
            let topFtToKickPoint = this.firstKickpoint - this.segment.boreholeTop;
            let feetToPixel = this.verticalToCurvePx / topFtToKickPoint;

            //bottom
            this.cementObject.bottomFt = this.segment.cementBottom;
            this.cementObject.bottomBeforeKickpoint = this.segment.cementBottom <= this.firstKickpoint;
            this.cementObject.bottomAfterKickpoint = this.segment.cementBottom > this.firstKickpoint;

            if (this.cementObject.bottomBeforeKickpoint) {
                let feetFromTop = this.segment.cementBottom - this.segment.boreholeTop;
                this.cementObject.bottomPx = (feetToPixel * feetFromTop) + this.canvasMarginTop;
            }
            if (this.cementObject.bottomAfterKickpoint) {
                this.cementObject.bottomPx = ((this.segment.cementBottom - this.firstKickpoint) / (this.segment.boreholeBottom - this.firstKickpoint) * this.horizontalAfterCurvePx) + this.curveEndPx;
            }
            //top
            this.cementObject.topFt = this.segment.cementTop;
            this.cementObject.topBeforeKickpoint = this.segment.cementTop < this.firstKickpoint;
            this.cementObject.topEndBeforeKickpoint = this.segment.cementBottom < this.firstKickpoint;

            if (this.cementObject.topBeforeKickpoint) {
                //do we have a cement top that is ABOVE the borehole top?
                if (this.cementObject.topFt < this.segment.boreholeTop) {
                    this.cementObject.topPx = this.canvasMarginTop;
                } else {
                    let feetFromTop = this.segment.cementTop - this.segment.boreholeTop;
                    this.cementObject.topPx = (feetToPixel * feetFromTop) + this.canvasMarginTop;
                }


            } else {
                //cement starts AFTER kick point
                this.cementObject.topPx = ((this.segment.cementTop - this.firstKickpoint) / (this.segment.boreholeBottom - this.firstKickpoint) * this.horizontalAfterCurvePx) + this.curveEndPx;
            }
            
        }

        //sort by location asc
        aryPlacement.sort(function (a, b) {
            return a.location - b.location
        })

        //array of drawing objects BEFORE kick point
        let beforeLocationArray = aryPlacement.filter(
            drawObject => drawObject.before == true);

        //array of drawing objects AFTER kick point
        let afterLocationArray = aryPlacement.filter(
            drawObject => drawObject.before == false);

        //calculate at top or bottom of the vertical string
        for (let drawObject of beforeLocationArray) {
            drawObject.atTop = drawObject.location === 0;
            drawObject.atBottom = drawObject.location === this.firstKickpoint; 
        }

        //calculate at top or bottom of the horizontal string
        for (let drawObject of afterLocationArray) {
            drawObject.atTop = drawObject.location === this.firstKickpoint; 
            drawObject.atBottom = drawObject.location === this.segment.boreholeBottom;
        }


        //Populate VERTICAL values BEFORE KP

        //get length of objects that aren't at the end or begining of the vertical string
        let beforeArrayLengthNotEnd = beforeLocationArray.filter(drawObject => drawObject.atTop != true && drawObject.atBottom != true).length;
        //calculate vertical padding
        let verticalPadding = this.verticalToCurvePx / beforeArrayLengthNotEnd;
        //Populate vertical position
        let count = 1;
        for (let drawObject of beforeLocationArray) {
            if (this.positionRelative) {
                if (!drawObject.atTop && !drawObject.atBottom) {
                    //There are spread out evenly in the vertical string (not proportional)
                    drawObject.canvasVerticalLocation = (count * verticalPadding) + this.canvasMarginTop;
                    count++;
                } else {
                    //must place these at TOP and BOTTOM of the vertical string.
                    drawObject.canvasVerticalLocation = ((drawObject.location / this.firstKickpoint) * this.verticalToCurvePx) + this.canvasMarginTop;
                }
            } else {

                drawObject.canvasVerticalLocation = ((drawObject.location / this.firstKickpoint) * this.verticalToCurvePx) + this.canvasMarginTop;
            }
        }

        //Populate HORIZONTAL values AFTER KP
        let letArrayLengthNotEnd = afterLocationArray.filter(drawObject => drawObject.atTop != true && drawObject.atBottom != true).length;
        //calculate horizontal padding
        let horizontalPadding = this.horizontalAfterCurvePx / letArrayLengthNotEnd;

        //populate horizontal positions
        let hCount = 1;
        for (let drawObject of afterLocationArray) {
            if (this.positionRelative) {
                if (!drawObject.atTop && !drawObject.atBottom) {
                    //spread out evenly horizontally
                    drawObject.canvasHorizontalLocation = (hCount * horizontalPadding) + this.curveEndPx;
                    hCount++;
                } else {
                    //must place at LEFT or RIGHT of the horizontal string!
                    drawObject.canvasHorizontalLocation = (((drawObject.location - this.firstKickpoint) / (this.segment.boreholeBottom - this.firstKickpoint)) * this.horizontalAfterCurvePx) + this.curveEndPx;
                }
            }
            else {
                drawObject.canvasHorizontalLocation = (((drawObject.location - this.firstKickpoint) / (this.segment.boreholeBottom - this.firstKickpoint)) * this.horizontalAfterCurvePx) + this.curveEndPx;
                
            }
        }

        //VERTICAL - place them on canvas
        for (let drawObject of beforeLocationArray) {
            switch (drawObject.typeNum) {
                case HorizontalDrawingParts.basket:
                    this.addBasketToCanvas(this.ctx, drawObject.canvasVerticalLocation, this.rightCasing, this.leftCasing, true);
                    break;
                case HorizontalDrawingParts.collar:
                    this.addCollarToCanvas(this.ctx, drawObject.canvasVerticalLocation, this.rightCasing, this.leftCasing - this.diagramCasingWidth, true);
                    break;
                case HorizontalDrawingParts.packer:
                    this.addPackerToCanvas(this.ctx, drawObject.canvasVerticalLocation, this.rightCasing - (this.packerW + this.innerCasingLineWidth), this.leftCasing, true)
                    break;
                case HorizontalDrawingParts.shoe:
                    //shoes forced to draw at the end of the casing (4/1/19)
                    this.addShoeToCanvas2(
                        this.ctx,
                        this.rightCasing,
                        this.leftCasing - this.shoeHW,
                        this.casingObject.bottomPx - this.shoeHW,
                        true);
                    break;
                default:
                    console.log("missed case in drawObject of beforeLocationArray " + drawObject.type);
            }
        }

        //HORIZONTAL
        for (let drawObject of afterLocationArray) {
            switch (drawObject.typeNum) {
                case HorizontalDrawingParts.basket:
                    this.addBasketToCanvas(this.ctx, drawObject.canvasHorizontalLocation - this.basketHW, this.horizontalTopPx + (2 * this.halfCasingWidthPx) + this.cementLineWidthPx, this.horizontalTopPx, false);
                    break;
                case HorizontalDrawingParts.collar:
                                                     //horizontal                                        //vertical right                                                          //vertical left
                    this.addCollarToCanvas(this.ctx, drawObject.canvasHorizontalLocation - this.packerW, this.horizontalTopPx + (2 * this.halfCasingWidthPx) + this.cementLineWidthPx, this.horizontalTopPx, false);
                    break;
                case HorizontalDrawingParts.packer:
                    this.addPackerToCanvas(this.ctx, drawObject.canvasHorizontalLocation - this.packerH, this.horizontalTopPx + (2 * this.halfCasingWidthPx), this.horizontalTopPx + this.cementLineWidthPx, false)
                    break;
                case HorizontalDrawingParts.shoe:
                    //shoes forced to draw at the end of the casing (4/1/19)
                    //img, x, y, width, height
                    this.addShoeToCanvas(
                        this.ctx,
                        this.casingObject.bottomPx - this.shoeHW,
                        this.horizontalTopPx,
                        this.horizontalTopPx + (2 * this.halfCasingWidthPx) + this.shoeHW,
                        false);                    
                    break;
                default:
                    console.log("missed case in drawObject of afterLocationArray " + drawObject.type);
            }
        }
    }

    /**
     * Helper to create image placement objects
     * @param type
     * @param value
     */
    getSimpleDrawObject(type: HorizontalDrawingParts, value: number): any{
        let returnObject: SimpleDrawObject = new SimpleDrawObject;

        returnObject.before = this.isRelativeToFirstKickpoint(type, false);
        returnObject.type = HorizontalDrawingParts[type];
        returnObject.typeNum = type;
        returnObject.location = value;

        return returnObject;
    }

    /**
     * Draws kick point label & line
     */
    addKickPointLabel(): void {
        //kickpoint label
        this.ctx.beginPath();
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.textAlign = "right";
        this.ctx.fillText("Kick Point", this.leftBorehole - this.labelPadding - this.groundLineWidth, this.curveTopPx + this.verticalTextOffset);
        this.ctx.fillText("(not to scale)", this.leftBorehole - this.labelPadding - this.groundLineWidth, this.curveTopPx + this.verticalTextOffset + 12);
        this.ctx.closePath();
        this.ctx.textAlign = "left";

        //kickpoint line
        this.ctx.beginPath();
        this.ctx.fillStyle = "#000000";
        this.ctx.lineWidth = 1;
        this.ctx.moveTo(this.leftBorehole, this.curveTopPx);
        this.ctx.lineTo(this.leftBorehole - this.groundLineWidth, this.curveTopPx);
        this.ctx.stroke();
        this.ctx.closePath();
    }

    /**
     * Fills string bottom with cement
     */
    addHorizontalCementBottom(): void {
        this.ctx.fillStyle = this.cementFillColor;
        this.ctx.beginPath();

        //inside the casing (horizontally oriented rectangle)
        this.ctx.fillRect(
            this.canvasWidth - (this.rightCanvasMarginPx + this.bottomCementWidth),
            this.canvasHeight - (this.diagramCasingWidth + (2 * this.halfCasingWidthPx)),
            this.bottomCementWidth,
            this.halfCasingWidthPx * 2);

        //end of string (vertically oriented rectangle)
        this.ctx.fillRect(
            this.canvasWidth - this.rightCanvasMarginPx,
            this.canvasHeight - this.bottomCanvasMarginPx + this.boreHoleCurveRadiusPx,
            this.canvasWidth - this.rightCanvasMarginPx,
            (this.halfCasingWidthPx * 2) + (this.diagramCasingWidth * 2)
        )
        this.ctx.closePath();
    }

    /**
     * Adds borehole with curve
     */
    addHorizontalBorehole(): void {

        let radius = this.boreHoleCurveRadiusPx;

        //right
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#000000";
        this.ctx.moveTo(this.rightBorehole, this.canvasMarginTop);
        this.ctx.lineWidth = 1;
        this.ctx.lineTo(this.rightBorehole, this.canvasHeight - this.bottomCanvasMarginPx);
        this.drawArc(radius, true, "borehole");
        this.ctx.lineTo(this.canvasWidth - this.rightCanvasMarginPx, this.canvasHeight - this.bottomCanvasMarginPx + radius);
        this.ctx.stroke();
        this.ctx.closePath();
        
        //left
        this.ctx.beginPath();
        this.ctx.moveTo(this.leftBorehole, this.canvasMarginTop);
        this.ctx.lineWidth = 1;
        this.ctx.lineTo(this.leftBorehole, this.canvasHeight - this.bottomCanvasMarginPx);
        this.drawArc(radius, false, "borehole");
        this.ctx.lineTo(this.canvasWidth - this.rightCanvasMarginPx, this.canvasHeight - this.bottomCanvasMarginPx + (2 * radius));
        this.ctx.stroke();
        this.ctx.closePath();


        //top label
        // left label position depends on if we are drawing the left ground rectangle
        let topLabelLeftLocation: number = this.leftBorehole - this.labelPadding;
        if (this.segmentIndex == 0) {
            topLabelLeftLocation -= this.groundLineWidth;
        }

        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.textAlign = "right";
        this.ctx.fillText("Top of " + this.segment.boreholeDiameter + " IN. Hole:", topLabelLeftLocation, this.canvasMarginTop + 4);
        this.ctx.fillText(this.segment.boreholeTop + " FT.", topLabelLeftLocation, this.canvasMarginTop + 16);
        //bottom label
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.fillText("Bottom of " + this.segment.boreholeDiameter + " IN. Hole: " + this.segment.boreholeBottom + " FT.", this.rightBorehole - this.labelPadding, this.canvasHeight - 3);

        this.ctx.beginPath();
        this.ctx.moveTo(this.rightBorehole + this.boreHoleCurveRadiusPx, this.canvasHeight);
        this.ctx.lineTo(this.rightBorehole, this.canvasHeight);
        this.ctx.stroke(); 
        this.ctx.closePath();

        //reset alignment for the rest of the diagram
        this.ctx.textAlign = "left";        

    }

    /**
     * Add casing with curve
     */
    addHorizontalCasing(casingObject: any): void {
        let radius = this.boreHoleCurveRadiusPx + this.diagramCasingWidth;

        //draw left then right casing STRINGS
        this.addSingleCasingString(true, casingObject);
        this.addSingleCasingString(false, casingObject);

        //label casing TOP
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        //move top casing text down so it does write over borehole label

        this.ctx.fillText(
            "Top of " + this.segment.casingDiameter + " IN. Casing: " + this.segment.casingTop + " FT.",
            this.rightBorehole + this.groundLineWidth + this.labelPadding,
            this.canvasMarginTop + this.verticalTextOffset
        );

        //label casing BOTTOM
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;

        //does the casing end before the first kickpoint?
        if (this.hasCasing(this.segment)) {
            if (casingObject.bottomBeforeKickpoint) {
                //draw the label off of the VERTICAL string
                this.ctx.fillText(
                    "Bottom of " + this.segment.casingDiameter + " IN. Casing: " + this.segment.casingBottom + " FT.",
                    this.rightBorehole + this.groundLineWidth + this.labelPadding,
                    casingObject.bottomPx
                );
            } else {
               
                //draw the label off of the HORIZONTAL string
                this.ctx.textAlign = "right";
    
                //split text into 2 lines
                this.ctx.fillText(
                    "Bottom of " + this.segment.casingDiameter + " IN.",
                    casingObject.bottomPx,
                    this.horizontalTopPx - (this.leading * 2)
                );
                this.ctx.fillText(
                    "Casing: " + this.segment.casingBottom + " FT.",
                    casingObject.bottomPx,
                    this.horizontalTopPx - this.leading
                );
                
            }
        }
       

        this.ctx.textAlign = "left";

    }

    /**
     * Draws single casing line (left or right) from top, through curve to bottom
     * @param left
     * @param casingObject
     */
    addSingleCasingString(left: boolean, casingObject:any): void {

        //vars for right
        let radius = this.boreHoleCurveRadiusPx + this.diagramCasingWidth;
        let casing = this.rightCasing;
        let finalLineTo = this.canvasHeight - (this.diagramCasingWidth + (2 * this.halfCasingWidthPx));
        let arcTypeRight = true;

        //vars for left
        if (left) {
            casing = this.leftCasing;
            finalLineTo = this.canvasHeight - this.diagramCasingWidth;
            radius = radius - (1.5 * this.diagramCasingWidth);
            arcTypeRight = false;
        }

        //draw line
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#000000";
        this.ctx.lineWidth = this.innerCasingLineWidth;

        //move to beginning of casing string at top
        this.ctx.moveTo(casing, casingObject.topPx);

        //does the casing end before the kick point?
        if (casingObject.bottomBeforeKickpoint) {

            //just draw it to the point before the kick point
            this.ctx.lineTo(casing, casingObject.bottomPx);

        } else {
            //draw VERTICAL line to top of curve
            this.ctx.lineTo(casing, this.curveTopPx);

            //is the bottom after the first kick point?
            if (casingObject.bottomAfterKickpoint) {

                //draw curve
                this.drawArc(radius, arcTypeRight, "casing");

                //draw HORIZONTAL line to string end
                this.ctx.lineTo(casingObject.bottomPx, finalLineTo);
            }
        }

        this.ctx.stroke();
        this.ctx.closePath();
    }

    /**
     * Fills cement with curve
     */
    addHorizontalCementFill(): void {

        let halfCementWidth = this.cementLineWidthPx * .5;
        let verticalRightCenter = this.rightCasing + halfCementWidth;
        let verticalLeftCenter = this.leftCasing - halfCementWidth;
        let radius = this.boreHoleCurveRadiusPx + this.diagramCasingWidth - halfCementWidth;

        //CEMENT TOP LABEL (only label top as of 3/15/19)
        if (this.cementObject.topBeforeKickpoint) {
            this.ctx.beginPath();
            this.ctx.font = this.font;
            this.ctx.fillStyle = this.fontFileStyle;
            this.ctx.textAlign = "left";
            if (this.cementObject.topPx < this.canvasMarginTop + this.leading) {
                
                this.ctx.fillText("Top of Cement: " + this.segment.cementTop + " FT.", this.rightBorehole + this.labelPadding + this.groundLineWidth, this.cementObject.topPx + this.leading);
            } else {
                this.ctx.fillText("Top of Cement: " + this.segment.cementTop + " FT.", this.rightBorehole + this.labelPadding, this.cementObject.topPx);
            }
            this.ctx.closePath();
        }

        //RIGHT CEMENT
        this.ctx.beginPath();

        this.ctx.moveTo(verticalRightCenter, this.cementObject.topPx);

        this.ctx.strokeStyle = this.cementFillColor;
        this.ctx.lineWidth = this.cementLineWidthPx;

        if (this.cementObject.topBeforeKickpoint) {
            //does the cement stop before the first kickpoint?
            if (this.cementObject.bottomBeforeKickpoint) {

                //draw vertical to BEFORE first kickpoint
                this.ctx.lineTo(verticalRightCenter, this.cementObject.bottomPx);

            } else {
                //draw vertical to first kickpoint
                this.ctx.lineTo(verticalRightCenter, this.verticalToCurvePx + this.canvasMarginTop);

                //does the cement go past the kickpoint?
                if (this.cementObject.bottomAfterKickpoint) {

                    //draw filled cement curve
                    this.drawArc(radius, true, "cement", false);

                    //draw horizontal filled cement
                    this.ctx.lineTo(this.cementObject.bottomPx, this.canvasHeight - (this.diagramCasingWidth + (2 * this.halfCasingWidthPx) + halfCementWidth));
                }
            }
        } else {
            //cement starts after kick point

            //move to after the kick point
            let startY = this.verticalToCurvePx + this.canvasMarginTop + this.radius + this.diagramCasingWidth + halfCementWidth;
            this.ctx.moveTo(this.cementObject.topPx, startY);
            this.ctx.lineTo(this.cementObject.bottomPx, startY);
        }

        this.ctx.stroke();
        this.ctx.closePath();

        //LEFT CEMENT
        this.ctx.beginPath();
        this.ctx.moveTo(verticalLeftCenter, this.cementObject.topPx);
        if (this.cementObject.topBeforeKickpoint) {
            //does the cement stop before the first kickpoint?
            if (this.cementObject.bottomBeforeKickpoint) {

                //draw vertical to BEFORE first kickpoint
                this.ctx.lineTo(verticalLeftCenter, this.cementObject.bottomPx);

            } else {

                //draw vertical to first kickpoint
                this.ctx.lineTo(verticalLeftCenter, this.verticalToCurvePx + this.canvasMarginTop);

                //does the cement go past the kickpoint?
                if (this.cementObject.bottomAfterKickpoint) {

                    //draw filled cement curve
                    this.drawArc(radius - this.diagramCasingWidth + (.5 * halfCementWidth), false, "cement", false);

                    //draw horizontal filled cement
                    this.ctx.lineTo(this.cementObject.bottomPx, this.canvasHeight - halfCementWidth);
                }
            }
        } else {
            //cement starts after kick point

            //move to after the kick point
            let startY = this.verticalToCurvePx + this.canvasMarginTop + this.radius + (2 * this.halfCasingWidthPx) + (2 * this.diagramCasingWidth) + halfCementWidth;
            this.ctx.moveTo(this.cementObject.topPx, startY);
            this.ctx.lineTo(this.cementObject.bottomPx, startY);
        }

        this.ctx.stroke();
        this.ctx.closePath();
    }

    /**
     * Adds unfilled cement 
     */
    addHorizontalEndCap(): void {

        this.ctx.beginPath();
        this.ctx.lineWidth = 1;
        this.ctx.strokeStyle = "#000000";

        //bottom left
        this.ctx.moveTo(this.canvasWidth - this.rightCanvasMarginPx, this.canvasHeight - this.bottomCanvasMarginPx + (2 * this.boreHoleCurveRadiusPx))
        //bottom right
        this.ctx.lineTo(this.canvasWidth - 1, this.canvasHeight - this.bottomCanvasMarginPx + (2 * this.boreHoleCurveRadiusPx));
        //top right
        this.ctx.lineTo(this.canvasWidth - 1, this.canvasHeight - this.bottomCanvasMarginPx + this.boreHoleCurveRadiusPx);
        //top left
        this.ctx.lineTo(this.canvasWidth - this.rightCanvasMarginPx, this.canvasHeight - this.bottomCanvasMarginPx + this.boreHoleCurveRadiusPx);

        this.ctx.stroke();
        this.ctx.closePath();
    }

    /**
     * Main method for adding curves to the canvas
     * @param radius
     * @param right
     * @param type
     * @param drawPoint
     */
    drawArc(radius: number, right: boolean, type: string, drawPoint: boolean = false): void {
        let leftX = 0;
        let rightX = 0;
        switch (type) {
            case "borehole":
                leftX = this.leftBorehole;
                rightX = this.rightBorehole;
                break;
            case "casing":
                leftX = this.leftCasing;
                rightX = this.rightCasing;
                break;
            case "cement":
                leftX = this.leftCasing - (this.cementLineWidthPx * .5);
                rightX = this.rightCasing + (this.cementLineWidthPx * .5);
                break;
            default:
                console.log("Missed case in drawArc " + type);
        }

        if (right) {
            this.ctx.arcTo(rightX, this.canvasHeight - this.bottomCanvasMarginPx + radius, rightX + radius, this.canvasHeight - this.bottomCanvasMarginPx + radius, radius);
        } else {
            this.ctx.arcTo(leftX, this.canvasHeight - this.bottomCanvasMarginPx + (2 * radius), rightX + radius, this.canvasHeight - this.bottomCanvasMarginPx + (2 * radius), 2 * radius);
        }
    }

    /**
     * Before or after the first kickpoint
     * @param partType
     * @param after
     */
    isRelativeToFirstKickpoint(partType: HorizontalDrawingParts, after:boolean): boolean {

        switch (partType) {
            case HorizontalDrawingParts.basket:
                if (after) {
                    return this.segment.basketDepth > this.firstKickpoint;
                } else {
                    return this.segment.basketDepth <= this.firstKickpoint;
                }
                
            case HorizontalDrawingParts.casing:
                if (after) {
                    return this.segment.casingBottom > this.firstKickpoint;    
                } else {
                    return this.segment.casingBottom < this.firstKickpoint;
                }
             
            case HorizontalDrawingParts.cement:
                if (after) {
                    return this.segment.cementBottom > this.firstKickpoint;
                } else {
                    return this.segment.cementBottom < this.firstKickpoint;
                }

            case HorizontalDrawingParts.collar:
                if (after) {
                    return this.segment.floatCollarDepth > this.firstKickpoint;
                } else {
                    return this.segment.floatCollarDepth <= this.firstKickpoint;
                }
                
            case HorizontalDrawingParts.shoe:
                //shoes always fall at the end of the casing 4/1/19
                if (after) {
                    return this.segment.casingBottom > this.firstKickpoint;
                } else {
                    return this.segment.casingBottom <= this.firstKickpoint;
                }

            case HorizontalDrawingParts.packer:
                if (after) {
                    return this.segment.packerDepth > this.firstKickpoint;
                } else {
                    return this.segment.packerDepth <= this.firstKickpoint;
                }

            default:
                console.log("missed case in isBeyondFirstKickpoint " + partType);
        }
    }

    /**
     * At top of the string
     * @param partType
     */
    isAtTop(partType: HorizontalDrawingParts): boolean {
        switch (partType) {

            case HorizontalDrawingParts.casing:
                return this.segment.casingTop <= this.segment.boreholeTop;
            case HorizontalDrawingParts.cement:
                return this.segment.cementTop <= this.segment.boreholeTop;
            default:
                console.log("missed case in isAtTop " + partType);
        }
    }

    /**
     * At bottom of the string
     * @param partType
     */
    isAtBottom(partType: HorizontalDrawingParts): boolean {
        switch (partType) {

            case HorizontalDrawingParts.casing:
                return this.segment.casingBottom == this.segment.boreholeBottom;
            case HorizontalDrawingParts.cement:
                return this.segment.cementBottom == this.segment.boreholeBottom;
            case HorizontalDrawingParts.shoe:
                return this.segment.casingShoeDepth == this.segment.boreholeBottom;
            default:
                console.log("missed case in isAtBottom " + partType);
        }
    }

}

